import styled from 'styled-components'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { SkeletonLine } from 'components/Skeleton'
import { RAG } from 'features/dashboard/charts/scoreColour'
import DashboardSection from './DashboardSection'
import LoadError from './LoadError'
import SeverityBreakdown from './SeverityBreakdown'
import { DASHBOARD_SECTIONS } from './remedialsQuery'
import { BORDER, INK, MUTED } from './ui'

const CARDS = [
  { key: 'allOpen', view: 'all_open', label: 'Open', explain: 'Still need putting right.', level: 'none', Icon: FormatListBulletedIcon },
  { key: 'overdue', view: 'overdue', label: 'Overdue', explain: 'Past their due date.', level: 'critical', Icon: WarningAmberIcon },
  { key: 'dueThisWeek', view: 'due_this_week', label: 'Due this week', explain: 'Due today or in the next 7 days.', level: 'medium', Icon: EventOutlinedIcon },
  { key: 'needsDecision', view: 'needs_decision', label: 'Needs a decision', explain: 'No task or completion yet.', level: 'none', Icon: HelpOutlineIcon },
  { key: 'taskInProgress', view: 'task_in_progress', label: 'Task in progress', explain: 'A task has been created to put it right.', level: 'none', Icon: PendingActionsOutlinedIcon },
  { key: 'resolvedLast30Days', view: 'resolved', label: 'Resolved (last 30 days)', explain: 'Put right in the last 30 days.', level: 'low', Icon: CheckCircleOutlineIcon },
]

const DashboardView = ({ counts, severityCounts, ready, statsError = false, onRetryStats, onRetrySection, sections, onOpenView, onOpenRemedial, showBuilding }) => (
  <Wrap>
    {statsError && <LoadError text='We couldn’t load the remedial totals. Please try again.' onRetry={onRetryStats} />}
    <Cards>
      {CARDS.map(card => {
        const value = counts[card.key]
        const rag = RAG[card.level]
        return (
          <Card
            key={card.key}
            type='button'
            onClick={() => onOpenView({ view: card.view })}
            aria-label={ready ? `${card.label}: ${value}. ${card.explain} Open this list.` : statsError ? `${card.label}: not loaded. Open this list.` : `${card.label}: loading`}
          >
            <IconTile style={{ color: rag.fg, background: rag.bg }} aria-hidden='true'>
              <card.Icon />
            </IconTile>
            <CardInfo>
              {ready
                ? <CardValue style={card.key === 'overdue' && value > 0 ? { color: RAG.critical.fg } : undefined}>{value}</CardValue>
                : statsError ? <CardValue aria-hidden='true'>?</CardValue> : <SkeletonLine $w='48px' $h={28} />}
              <CardLabel>{card.label}</CardLabel>
              <CardExplain>{card.explain}</CardExplain>
            </CardInfo>
          </Card>
        )
      })}
    </Cards>
    <Columns>
      <Sections>
        {DASHBOARD_SECTIONS.map(section => (
          <DashboardSection
            key={section.key}
            section={section}
            state={sections[section.key]}
            onOpenView={onOpenView}
            onOpenRemedial={onOpenRemedial}
            onRetry={onRetrySection}
            showBuilding={showBuilding}
          />
        ))}
      </Sections>
      <SeverityBreakdown counts={severityCounts} ready={ready} failed={statsError} onOpenView={onOpenView} />
    </Columns>
  </Wrap>
)

export default DashboardView

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`

const Card = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 96px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease;
  &:hover { border-color: #a1a1aa; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
`

const IconTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  svg { font-size: 24px; }
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    svg { font-size: 20px; }
  }
`

const CardInfo = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const CardLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const CardValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
  color: ${INK};
`

const CardExplain = styled.span`
  font-size: 14px;
  color: ${MUTED};
`

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
  gap: 16px;
  align-items: start;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
